import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowRightOutlined,
  CheckCircleOutlined,
  DatabaseOutlined,
  MailOutlined,
  RadarChartOutlined,
  SearchOutlined,
  ThunderboltOutlined,
  RightOutlined
} from '@ant-design/icons';
import { Button, Select, message } from 'antd';
import styles from './index.module.less';
import gongwanganbeiIcon from '../../assets/imgs/gongwanganbei.png';
import logoIcon from '../../assets/imgs/logo.png';

type Lang = 'zh' | 'en';

type CopySet = {
  navBrand: string;
  navPhaseTag: string;
  navProduct: string;
  navClient: string;
  navPricing: string;
  navContact: string;
  navLogin: string;
  heroKicker: string;
  heroTitle: string;
  heroSubTitle: string;
  heroPrimaryCta: string;
  heroSecondaryCta: string;
  moduleTag: string;
  featureSectionTitle: string;
  featureSectionSubTitle: string;
  installTag: string;
  installTitle: string;
  installSubTitle: string;
  installNotice: string;
  installPrimaryCta: string;
  installSecondaryCta: string;
  pricingTitle: string;
  pricingSubTitle: string;
  footerTitle: string;
  footerSubTitle: string;
  footerLegal: string;
};

const copy: Record<Lang, CopySet> = {
  zh: {
    navBrand: 'HRROOMIE',
    navPhaseTag: '测试期',
    navProduct: '产品矩阵',
    navClient: 'HRRoomie客户端',
    navPricing: '方案与定价',
    navContact: '联系我们',
    navLogin: '控制台登录',
    heroKicker: 'AI RECRUITMENT AGENT CLOSED-LOOP',
    heroTitle: 'HRROOMIE: 实时搜索互联网为您寻找人才',
    heroSubTitle:
      '用最少的时间招募最多最优的人才。HRROOMIE 把智能搜寻、精准匹配、自动联系、反馈通知和状态跟进串成一个全流程 Agent 代理闭环。',
    heroPrimaryCta: '立即体验 AI 闭环',
    heroSecondaryCta: '查看能力模块',
    moduleTag: 'PRODUCT MODULES',
    featureSectionTitle: '多智能体协同合作',
    featureSectionSubTitle:
      '由五个智能体组成：人才搜寻Agent、人才洞察Agent、邮件触达Agent、反馈跟进Agent和状态跟进Agent。',
    installTag: 'DESKTOP WORKSTATION',
    installTitle: '安装 HRRoomie 客户端，让电脑具备自动化人才搜寻能力',
    installSubTitle:
      '下载安装客户端后，即可在本地一键启动人才搜寻、智能匹配和触达流程。适合招聘顾问、业务负责人和 HR 团队协同使用。',
    installNotice: '产品测试期，暂时限制试用，敬请期待',
    installPrimaryCta: '立即安装客户端',
    installSecondaryCta: '登录控制台',
    pricingTitle: 'AI 时代，用 AI 的效率决定服务的价格',
    pricingSubTitle:
      '覆盖产品体验、岗位测试、长期寻人、额度扩展和新职位解锁等场景，成一单获百倍收益。',
    footerTitle: '联系我们，开始搭建你的人才搜寻引擎',
    footerSubTitle:
      '如果你想要更快的搜寻、更准的匹配、更稳的联系和更自动化的反馈闭环，请把需求发给我们。',
    footerLegal: '© 2025-2026 盖猎信息技术. 保留所有权利。'
  },
  en: {
    navBrand: 'HRROOMIE',
    navPhaseTag: 'Beta',
    navProduct: 'Product',
    navClient: 'HRRoomie Client',
    navPricing: 'Pricing',
    navContact: 'Contact',
    navLogin: 'Console Log In',
    heroKicker: 'AI RECRUITMENT AGENT CLOSED-LOOP',
    heroTitle: 'HRROOMIE: Your Intelligent Sourcing Partner',
    heroSubTitle:
      'Source more and better talent in less time. HRROOMIE connects smart search, precise matching, outreach, and status tracking into a full-process Agent workflow.',
    heroPrimaryCta: 'Start AI Workflow',
    heroSecondaryCta: 'Explore Modules',
    moduleTag: 'PRODUCT MODULES',
    featureSectionTitle: 'Multi-Agent Collaboration',
    featureSectionSubTitle:
      'Built with 5 core agents: Sourcing, Insight, Outreach, Feedback, and Status tracking.',
    installTag: 'DESKTOP WORKSTATION',
    installTitle:
      'Install HRRoomie to turn your PC into a talent sourcing station',
    installSubTitle:
      'Launch sourcing, intelligent matching, and outreach workflows locally in one place. Designed for recruiters and HR teams.',
    installNotice:
      'Preview period: trial access is temporarily limited. Stay tuned.',
    installPrimaryCta: 'Download Workstation',
    installSecondaryCta: 'Console Login',
    pricingTitle:
      'In the AI era, service pricing is determined by AI efficiency',
    pricingSubTitle:
      'Covering trials, role tests, long-term sourcing, quota expansion, and new-role unlocks.',
    footerTitle: "Let's build your talent sourcing engine",
    footerSubTitle:
      'Send us your requirements for faster sourcing, sharper matching, and automated follow-ups.',
    footerLegal:
      '© 2025-2026 GAILIE Information Technology. All rights reserved.'
  }
};

const featureCards = [
  {
    id: 'feature-posting',
    icon: <SearchOutlined />,
    zhTitle: '人才搜寻 Agent',
    enTitle: 'Talent Sourcing Agent',
    zhDesc:
      '自动在技术交流平台、技术分享平台和主流招聘平台中搜寻候选人，聚合可触达的人才线索。',
    enDesc:
      'Searches talent across technical communities and hiring boards, aggregating reachable leads.',
    tag: 'DISCOVERY'
  },
  {
    id: 'feature-sourcing',
    icon: <RadarChartOutlined />,
    zhTitle: '人才洞察 Agent',
    enTitle: 'Talent Insight Agent',
    zhDesc:
      '对候选人的技能、经历、匹配度和岗位契合度做智能分析，输出清晰的人才洞察报告。',
    enDesc:
      'Analyzes skills, experience, and fit to produce clear talent insight reports.',
    tag: 'ANALYTICS'
  },
  {
    id: 'feature-matching',
    icon: <ThunderboltOutlined />,
    zhTitle: '邮件触达 Agent',
    enTitle: 'Outreach Agent',
    zhDesc: '根据候选人画像与上下文自动生成个性化邮件，支持批量与高频沟通。',
    enDesc:
      'Generates context-aware outreach emails and manages scheduled touchpoints.',
    tag: 'ENGAGEMENT'
  },
  {
    id: 'feature-pool',
    icon: <DatabaseOutlined />,
    zhTitle: '反馈跟进 Agent',
    enTitle: 'Feedback Follow-up Agent',
    zhDesc: '自动收集候选人回复与关键互动，及时整理并推送到后续跟进环节。',
    enDesc:
      'Collects replies and interactions automatically to coordinate follow-ups.',
    tag: 'TRIAGE'
  },
  {
    id: 'feature-status',
    icon: <CheckCircleOutlined />,
    zhTitle: '状态跟进 Agent',
    enTitle: 'Status Tracking Agent',
    zhDesc:
      '持续跟踪候选人在各个阶段的状态变化，推动流程从搜寻到转化闭环完成。',
    enDesc:
      'Tracks candidate status through all stages to close the conversion loop.',
    tag: 'CONVERSION'
  }
];

const flowSteps = [
  {
    key: 'source',
    zh: '智能搜寻',
    en: 'Sourcing',
    descZh: '多平台自动抓取与去重',
    descEn: 'Multi-platform discovery'
  },
  {
    key: 'match',
    zh: '精准匹配',
    en: 'Matching',
    descZh: '向量高维召回与大模型精筛',
    descEn: 'Vector recall & LLM fit'
  },
  {
    key: 'contact',
    zh: '自动联系',
    en: 'Outreach',
    descZh: '生成触达话术与批量沟通',
    descEn: 'Personalized sequences'
  },
  {
    key: 'notify',
    zh: '反馈通知',
    en: 'Triage',
    descZh: '实时回复提醒与意向分类',
    descEn: 'Intent & reply parsing'
  },
  {
    key: 'follow',
    zh: '状态跟进',
    en: 'Status',
    descZh: '阶段自动推进与闭环转化',
    descEn: 'Automated stage loop'
  }
];

const pricingPlans = [
  {
    id: 'monthly',
    zhName: '19.9 月付体验版',
    enName: '19.9 Monthly',
    price: '¥19.9',
    unit: '/ 月',
    descZh: '适合轻量试用，快速体验智能搜寻、匹配和触达能力。',
    descEn: 'For lightweight trials of sourcing and outreach.'
  },
  {
    id: 'semiannual',
    zhName: '99 半年付推荐版',
    enName: '99 Semiannual',
    price: '¥99',
    unit: '/ 6个月',
    descZh: '适合连续使用，覆盖岗位测试、短周期招聘和阶段性寻人。',
    descEn: 'For recurring use across role tests and short cycles.'
  },
  {
    id: 'annual',
    zhName: '299 年度标准版',
    enName: '299 Annual',
    price: '¥299',
    unit: '/ 年',
    descZh: '适合长期使用，支持智能匹配、联系与反馈通知全闭环。',
    descEn: 'For long-term sourcing with full automation.'
  },
  {
    id: 'deluxe',
    featured: true,
    zhName: '999 豪华包年版',
    enName: '999 Deluxe Annual',
    price: '¥999',
    unit: '/ 年',
    descZh: '适合高频招聘团队，支持更高额度与更强 Agent 代理能力。',
    descEn: 'Higher quotas and priority compute for HR teams.'
  }
];

const HomePage: React.FC = () => {
  const navigate = useNavigate();
  const [lang, setLang] = useState<Lang>('zh');
  const hostname = window.location.hostname.toLowerCase();
  const isCnDomain = hostname.endsWith('.cn');
  const showLanguageSelector = !isCnDomain;

  useEffect(() => {
    if (isCnDomain) {
      setLang('zh');
      localStorage.setItem('hrr_lang', 'zh');
      return;
    }

    const saved = localStorage.getItem('hrr_lang') as Lang | null;
    if (saved === 'zh' || saved === 'en') setLang(saved);
  }, [isCnDomain]);

  const changeLang = (l: Lang) => {
    if (isCnDomain && l !== 'zh') return;
    setLang(l);
    localStorage.setItem('hrr_lang', l);
  };

  const text = copy[lang];

  const goToSection = (id: string) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
  };

  const goToConsoleLogin = () => {
    const baseUrl = (process.env.REACT_APP_API_URL || '').replace(/\/$/, '');
    if (!baseUrl) {
      navigate('/login');
      return;
    }

    window.location.href = `${baseUrl}/login`;
  };

  return (
    <div className={styles.page}>
      <div className={styles.ambientGlow} />

      {/* Navigation */}
      <header className={styles.navBar}>
        <div className={styles.navInner}>
          <div className={styles.brand} onClick={() => goToSection('hero')}>
            <img
              src={logoIcon}
              alt="HRROOMIE logo"
              className={styles.brandIcon}
            />
            <span className={styles.brandText}>{text.navBrand}</span>
            <span className={styles.brandPhaseTag}>{text.navPhaseTag}</span>
          </div>

          <nav className={styles.navLinks}>
            <button
              className={styles.navLink}
              onClick={() => goToSection('features')}
            >
              {text.navProduct}
            </button>
            <button
              className={styles.navLink}
              onClick={() => goToSection('install')}
            >
              {text.navClient}
            </button>
            <button
              className={styles.navLink}
              onClick={() => goToSection('pricing')}
            >
              {text.navPricing}
            </button>
            <button
              className={styles.navLink}
              onClick={() => goToSection('contact')}
            >
              {text.navContact}
            </button>
          </nav>

          <div className={styles.navActions}>
            {showLanguageSelector ? (
              <Select
                className={styles.langSelect}
                value={lang}
                onChange={(value) => changeLang(value as Lang)}
                options={[
                  { value: 'zh', label: '简体中文' },
                  { value: 'en', label: 'English' }
                ]}
              />
            ) : null}
            <Button
              type="primary"
              shape="round"
              className={styles.primaryBtn}
              onClick={goToConsoleLogin}
            >
              {text.navLogin}
            </Button>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <main className={styles.main}>
        <section id="hero" className={styles.heroSection}>
          <div className={styles.heroBadge}>
            <span className={styles.heroBadgeDot} />
            {text.heroKicker}
          </div>

          <h1 className={styles.heroTitle}>{text.heroTitle}</h1>
          <p className={styles.heroSubTitle}>{text.heroSubTitle}</p>

          <div className={styles.heroCtaGroup}>
            <Button
              type="primary"
              size="large"
              shape="round"
              className={styles.heroPrimaryBtn}
              onClick={goToConsoleLogin}
            >
              {text.heroPrimaryCta} <ArrowRightOutlined />
            </Button>
            <Button
              size="large"
              shape="round"
              className={styles.heroSecondaryBtn}
              onClick={() => goToSection('features')}
            >
              {text.heroSecondaryCta}
            </Button>
          </div>

          {/* Workflow Showcase */}
          <div className={styles.pipelineContainer}>
            <div className={styles.pipelineHeader}>
              <span className={styles.nodeTag}>HUMAN</span>
              <span className={styles.nodeTitle}>发布职位需求</span>
              <div className={styles.pulseConnector} />
            </div>

            <div className={styles.agentZone}>
              <div className={styles.agentZoneBar}>
                <span className={styles.agentZoneTitle}>
                  <ThunderboltOutlined className={styles.flashIcon} /> AI
                  自主执行区
                </span>
                <span className={styles.agentZoneStatus}>ACTIVE LOOP</span>
              </div>

              <div className={styles.pipelineFlow}>
                {flowSteps.map((step, idx) => (
                  <div key={step.key} className={styles.flowNode}>
                    <div className={styles.nodeMeta}>
                      <span className={styles.nodeIndex}>0{idx + 1}</span>
                      <span className={styles.nodeName}>
                        {lang === 'zh' ? step.zh : step.en}
                      </span>
                    </div>
                    <p className={styles.nodeDesc}>
                      {lang === 'zh' ? step.descZh : step.descEn}
                    </p>
                    {idx < flowSteps.length - 1 && (
                      <div className={styles.flowArrow}>
                        <RightOutlined />
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>

            <div className={styles.pipelineHeader}>
              <div className={styles.pulseConnector} />
              <span className={styles.nodeTag}>HUMAN</span>
              <span className={styles.nodeTitle}>交付与跟进闭环</span>
            </div>
          </div>
        </section>

        {/* Features Section */}
        <section id="features" className={styles.featureSection}>
          <div className={styles.sectionHeader}>
            <span className={styles.sectionTag}>{text.moduleTag}</span>
            <h2 className={styles.sectionTitle}>{text.featureSectionTitle}</h2>
            <p className={styles.sectionSubTitle}>
              {text.featureSectionSubTitle}
            </p>
          </div>

          <div className={styles.featureGrid}>
            {featureCards.map((card) => (
              <div key={card.id} className={styles.featureCard}>
                <div className={styles.featureTop}>
                  <div className={styles.featureIcon}>{card.icon}</div>
                  <span className={styles.featureTag}>{card.tag}</span>
                </div>
                <h3 className={styles.featureTitle}>
                  {lang === 'zh' ? card.zhTitle : card.enTitle}
                </h3>
                <p className={styles.featureDesc}>
                  {lang === 'zh' ? card.zhDesc : card.enDesc}
                </p>
              </div>
            ))}
          </div>
        </section>

        {/* Client Installation Banner */}
        <section id="install" className={styles.installSection}>
          <div className={styles.installPanel}>
            <div className={styles.installNotice}>{text.installNotice}</div>
            <div className={styles.installInfo}>
              <span className={styles.installTag}>{text.installTag}</span>
              <h2 className={styles.installTitle}>{text.installTitle}</h2>
              <p className={styles.installSubTitle}>{text.installSubTitle}</p>
              <div className={styles.installCtaGroup}>
                <Button
                  type="primary"
                  size="large"
                  shape="round"
                  className={styles.installPrimaryBtn}
                  onClick={() =>
                    message.info('产品测试期，暂时限制试用，敬请期待')
                  }
                >
                  {text.installPrimaryCta}
                </Button>
                <Button
                  size="large"
                  shape="round"
                  className={styles.installSecondaryBtn}
                  onClick={goToConsoleLogin}
                >
                  {text.installSecondaryCta}
                </Button>
              </div>
            </div>
          </div>
        </section>

        {/* Pricing Section */}
        <section id="pricing" className={styles.pricingSection}>
          <div className={styles.sectionHeader}>
            <span className={styles.sectionTag}>PRICING</span>
            <h2 className={styles.sectionTitle}>{text.pricingTitle}</h2>
            <p className={styles.sectionSubTitle}>{text.pricingSubTitle}</p>
          </div>

          <div className={styles.pricingGrid}>
            {pricingPlans.map((plan) => (
              <div
                key={plan.id}
                className={`${styles.pricingCard} ${plan.featured ? styles.featured : ''}`}
              >
                {plan.featured && (
                  <div className={styles.recommendedBadge}>推荐</div>
                )}
                <h3 className={styles.planName}>
                  {lang === 'zh' ? plan.zhName : plan.enName}
                </h3>
                <div className={styles.planPriceRow}>
                  <span className={styles.priceVal}>{plan.price}</span>
                  <span className={styles.priceUnit}>{plan.unit}</span>
                </div>
                <p className={styles.planDesc}>
                  {lang === 'zh' ? plan.descZh : plan.descEn}
                </p>
                <Button
                  type={plan.featured ? 'primary' : 'default'}
                  shape="round"
                  className={
                    plan.featured ? styles.primaryBtn : styles.outlineBtn
                  }
                  block
                  onClick={goToConsoleLogin}
                >
                  立即体验
                </Button>
              </div>
            ))}
          </div>
        </section>

        {/* Contact Section */}
        <section id="contact" className={styles.contactSection}>
          <div className={styles.contactPanel}>
            <h2 className={styles.contactTitle}>{text.footerTitle}</h2>
            <p className={styles.contactSubTitle}>{text.footerSubTitle}</p>
            <div className={styles.emailBadge}>
              <MailOutlined /> service@hrroomie.cn
            </div>
          </div>
        </section>
      </main>

      {/* Footer */}
      <footer className={styles.footer}>
        <div className={styles.footerInner}>
          <span>{text.footerLegal}</span>
          <div className={styles.legalLinks}>
            <a
              href="https://beian.mps.gov.cn/#/query/webSearch?code=11010502056798"
              target="_blank"
              rel="noreferrer"
              className={styles.legalPoliceLink}
            >
              <img
                src={gongwanganbeiIcon}
                alt="公安网安备"
                className={styles.legalPoliceIcon}
              />
              京公网安备11010502056798号
            </a>
            <span className={styles.divider}>|</span>
            <a
              href="https://beian.miit.gov.cn/"
              target="_blank"
              rel="noreferrer"
            >
              京ICP备2025116878号-1
            </a>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default HomePage;
